// Where a sales order stands between Sales and Assembly. Sales builds it in the configurator and confirms it;
// Assembly reviews and accepts it (and every new revision of it), then marks it In production when the boat
// starts being built from it. A confirmed order is locked for Sales: only Assembly (or an administrator) changes
// it, unless Assembly opens it for Sales, for one new revision, with a reason and an optional time limit.
const COMMITTED = new Set(['confirmed', 'changed', 'locked', 'completed']);
/** a confirmed order (any of its committed states) */
export const isCommittedOrder = (status) => COMMITTED.has(status);
/** whether Assembly still has to accept the order's latest revision */
export function awaitsAcceptance(o) {
    return isCommittedOrder(o.status) && (o.acceptedRevision ?? null) !== o.revisionNumber;
}
/** the order's stage: an order in production that came back with a new revision still reads In production
 *  (awaitsAcceptance says it needs accepting again) */
export function orderStage(o) {
    if (o.status === 'deleted' || o.status === 'cancelled')
        return 'deleted';
    if (!isCommittedOrder(o.status))
        return 'draft';
    if (o.inProduction)
        return 'in_production';
    if (o.acceptedRevision == null)
        return 'to_accept';
    return o.acceptedRevision === o.revisionNumber ? 'accepted' : 'changed';
}
/** each stage as Sales and as Assembly read it, and its status tone (pill kind) */
export const ORDER_STAGES = {
    draft: { sales: 'Draft', assembly: 'Draft', tone: '', hint: 'Still being built in the configurator' },
    to_accept: { sales: 'Waiting for Assembly', assembly: 'To accept', tone: 'warn', hint: 'Confirmed by Sales; Assembly hasn’t accepted it yet' },
    changed: { sales: 'Revised, waiting', assembly: 'Changed', tone: 'warn', hint: 'A new revision Assembly hasn’t accepted yet' },
    accepted: { sales: 'Accepted', assembly: 'Accepted', tone: 'ok', hint: 'Assembly accepted the latest revision' },
    in_production: { sales: 'In production', assembly: 'In production', tone: 'info', hint: 'The boat is being built from this order: it is locked' },
    deleted: { sales: 'Deleted', assembly: 'Deleted', tone: 'bad', hint: 'Deleted' },
};
/** whether the order is open for Sales right now (its opening hasn't run out) */
export function salesOpenNow(open, now = Date.now()) {
    if (!open)
        return false;
    if (!open.until)
        return true;
    const t = Date.parse(open.until.includes('T') ? open.until : `${open.until.replace(' ', 'T')}Z`);
    return !Number.isFinite(t) || t > now;
}
/**
 * Who may change a confirmed order's configuration (a new revision): Assembly's lock managers and
 * administrators always; anyone else who can edit orders only while Assembly has it open for Sales, and never
 * while it is in production. A draft is the configurator's own business (its creator, or an administrator).
 */
export function canReviseOrder(o, can, now = Date.now()) {
    if (!isCommittedOrder(o.status))
        return { ok: true, why: '' };
    if (can('orderlock:manage') || can('admin:users'))
        return { ok: true, why: '' };
    if (!can('order:edit') && !can('order:edit_unlocked'))
        return { ok: false, why: 'You can’t change confirmed orders.' };
    if (o.inProduction)
        return { ok: false, why: 'It is in production: only Assembly can change it now.' };
    if (!salesOpenNow(o.salesOpen, now))
        return { ok: false, why: 'It is locked: Assembly can open it for you to change it.' };
    return { ok: true, why: '' };
}
//# sourceMappingURL=orderStage.js.map